import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Radio, RadioGroup, FormControlLabel, FormControl, Paper,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Configuración del remitente de los correos ATS del proyecto activo.
 * mode: 'default' (Amanda HR) | 'user' (usuario que envía) | 'custom' (texto libre).
 * Solo cambia el nombre visible; la dirección de envío no es configurable.
 */
const AtsEmailSenderTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert, user } = useStore.getState();

  const [mode, setMode] = useState('default');
  const [customName, setCustomName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const userName = user?.name || user?.displayName || '';

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/projects/ats-email-sender`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) {
          setMode(data.mode || 'default');
          setCustomName(data.customName || '');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.emailSender.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put(
        `/projects/ats-email-sender`,
        { mode, customName },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        showAlert(t('ats.config.emailSender.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.emailSender.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.emailSender.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const previewName =
    mode === 'custom' ? (customName.trim() || 'Amanda HR')
      : mode === 'user' ? (userName || customName.trim() || 'Amanda HR')
        : 'Amanda HR';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.emailSender.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('ats.config.emailSender.description')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t('ats.config.emailSender.note')}
      </Typography>

      <FormControl sx={{ mb: 2 }}>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="default"
            control={<Radio />}
            label={t('ats.config.emailSender.modeDefault')}
          />
          <FormControlLabel
            value="user"
            control={<Radio />}
            label={t('ats.config.emailSender.modeUser')}
          />
          {mode === 'user' && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {t('ats.config.emailSender.modeUserHint')}
            </Typography>
          )}
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label={t('ats.config.emailSender.modeCustom')}
          />
        </RadioGroup>
      </FormControl>

      {(mode === 'custom' || mode === 'user') && (
        <TextField
          fullWidth
          label={t('ats.config.emailSender.customName')}
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ mb: 2 }}
        />
      )}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#f9f9f9' }}>
        <Typography variant="caption" color="text.secondary">
          {t('ats.config.emailSender.preview')}:
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {previewName} &lt;noreply@amandahr.ai&gt;
        </Typography>
      </Paper>

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.emailSender.save')}
      </Button>
    </Box>
  );
};

export default AtsEmailSenderTab;
